import React from "react";
import { Trophy, Coins } from "lucide-react";
import winnerHeaderImg from "assets/images/giveaway/winnerheader.png";
import crownImg from "assets/images/giveaway/firstCrown.png";
import firstPrizeImg from "assets/images/giveaway/firstPrize.png";
import secondPrizeImg from "assets/images/giveaway/secondPrize.png";
import thirdPrizeImg from "assets/images/giveaway/thirdPrize.png";

export interface PreviewCategory {
  title: string; // e.g. "1st Prize", "2nd Prize"
  /** 1/2/3 for a prize-tier category — drives podium placement. Omitted for Additional Winners. */
  slot?: 1 | 2 | 3;
  reward?: string;
  tokenReward?: number;
  winners: Array<{
    userId: number;
    userName: string;
    profilePictureUrl?: string;
  }>;
}

interface WinnerPreviewCardProps {
  categories: PreviewCategory[];
}

/** "John D" -> "JD", "Priya Nair" -> "PN". */
const toInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const podiumStyle: Record<1 | 2 | 3, { avatarSize: string; blockImg: string }> = {
  1: { avatarSize: "w-16 h-16", blockImg: firstPrizeImg },
  2: { avatarSize: "w-12 h-12", blockImg: secondPrizeImg },
  3: { avatarSize: "w-12 h-12", blockImg: thirdPrizeImg },
};

const WinnerAvatar: React.FC<{
  name: string;
  profilePictureUrl?: string;
  size: string;
}> = ({ name, profilePictureUrl, size }) =>
  profilePictureUrl ? (
    <img
      src={profilePictureUrl}
      alt={name}
      className={`${size} rounded-full object-cover border-2 border-white shadow-sm shrink-0`}
    />
  ) : (
    <div
      className={`${size} rounded-full bg-transparent text-violet-700 font-bold text-[14px] flex items-center justify-center shadow-sm border-2 border-white shrink-0`}
    >
      {toInitials(name) || "?"}
    </div>
  );

/**
 * Winner announcement preview card matching the user home screen display.
 * Renders a neutral placeholder when empty, and a podium (1st/2nd/3rd) plus
 * an Additional Winners list when winners are selected.
 */
const WinnerPreviewCard: React.FC<WinnerPreviewCardProps> = ({ categories }) => {
  const activeCategories = categories.filter((cat) => cat.winners.length > 0);
  const hasWinners = activeCategories.length > 0;

  if (!hasWinners) {
    return (
      <div className="relative w-full max-w-[380px] mx-auto min-h-[360px] rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/70 p-6 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 rounded-full bg-violet-50 border border-violet-100 flex items-center justify-center text-violet-500 mb-4 shadow-sm">
          <Trophy className="w-8 h-8 opacity-60" />
        </div>
        <h4 className="text-base font-bold text-grease-100 mb-1">
          Winner Announcement Preview
        </h4>
        <p className="text-xs text-lightgray-200 leading-relaxed max-w-[240px]">
          Select one or more winners above to preview the real-time announcement card.
        </p>
      </div>
    );
  }

  // Podium slots — 2nd, 1st, 3rd left-to-right — only rendered when that tier has a winner.
  const podiumCategories = ([2, 1, 3] as const)
    .map((slot) => activeCategories.find((cat) => cat.slot === slot))
    .filter((cat): cat is PreviewCategory => !!cat);

  const additionalCategory = activeCategories.find((cat) => !cat.slot);

  return (
    <div className="relative w-full max-w-[380px] mx-auto rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/70 p-4">
      <div className="rounded-xl overflow-hidden shadow-lg bg-white">
        {/* Header */}
        <img src={winnerHeaderImg} alt="Giveaway Winner — Congratulations!" className="w-full h-auto block" />

        {/* Podium */}
        {podiumCategories.length > 0 && (
          <div className="flex items-end justify-center gap-0 px-5 pt-8 pb-4 bg-gray-50">
            {podiumCategories.map((cat) => {
              const winner = cat.winners[0];
              const slot = cat.slot!;
              const style = podiumStyle[slot];
              return (
                <div key={cat.title} className="flex flex-col items-center w-20">
                  <div className="relative mb-1.5">
                    {slot === 1 && (
                      <img
                        src={crownImg}
                        alt=""
                        className="w-9 h-9 absolute top-[-0.80rem] left-[90%] -translate-x-1/2 drop-shadow"
                        aria-hidden="true"
                      />
                    )}
                    <WinnerAvatar
                      name={winner.userName}
                      profilePictureUrl={winner.profilePictureUrl}
                      size={style.avatarSize}
                    />
                  </div>
                  <p className="text-xs font-semibold text-grease-100 truncate max-w-full">
                    {winner.userName}
                  </p>
                  {cat.reward && (
                    <span className="mt-1 inline-block max-w-full truncate rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[10px] font-semibold text-grease-100 shadow-sm">
                      {cat.reward}
                    </span>
                  )}
                  <img src={style.blockImg} alt={`${slot}`} className="mt-2 w-full h-auto" />
                </div>
              );
            })}
          </div>
        )}

        {/* Additional Winners */}
        {additionalCategory && (
          <div className="px-4 pb-4 pt-3 bg-gray-50 border-t border-gray-100 space-y-2">
            <span className="text-[11px] font-bold text-lightgray-200 uppercase tracking-wide">
              {additionalCategory.title}
            </span>
            <div className="flex flex-col gap-2 max-h-36 overflow-y-auto pr-1">
              {additionalCategory.winners.map((winner) => (
                <div
                  key={winner.userId}
                  className="inline-flex items-center gap-2 bg-white border border-gray-200 pl-1.5 pr-2.5 py-1 rounded-full shadow-sm"
                >
                  <WinnerAvatar
                    name={winner.userName}
                    profilePictureUrl={winner.profilePictureUrl}
                    size="w-6 h-6"
                  />
                  <span className="text-xs font-semibold text-grease-100 truncate max-w-[110px]">
                    {winner.userName}
                  </span>
                  {additionalCategory.tokenReward && Number(additionalCategory.tokenReward) > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-600">
                      <Coins className="w-3 h-3" />+{additionalCategory.tokenReward}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WinnerPreviewCard;
